import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => localStorage.getItem('m3_token'));

  const ROLE_PERMISSIONS = {
    owner: { can_edit: true, can_delete: true, can_manage_users: true, can_view_costs: true, can_export: true },
    admin: { can_edit: true, can_delete: true, can_manage_users: true, can_view_costs: true, can_export: true },
    chef: { can_edit: true, can_delete: false, can_manage_users: false, can_view_costs: false, can_export: false },
    viewer: { can_edit: false, can_delete: false, can_manage_users: false, can_view_costs: false, can_export: false },
  };

  const permissions = user ? (ROLE_PERMISSIONS[user.role] || ROLE_PERMISSIONS.viewer) : ROLE_PERMISSIONS.viewer;

  const axiosAuth = useCallback(() => {
    const headers = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return axios.create({ baseURL: API, headers });
  }, [token]);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    try {
      const res = await axios.get(`${API}/auth/me`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setUser(res.data);
    } catch {
      setUser(null);
      localStorage.removeItem('m3_token');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    const { token: t, user: u } = res.data;
    localStorage.setItem('m3_token', t);
    setToken(t);
    setUser(u);
    return u;
  };

  const register = async (data) => {
    const res = await axios.post(`${API}/auth/register`, data);
    const { token: t, user: u } = res.data;
    localStorage.setItem('m3_token', t);
    setToken(t);
    setUser(u);
    return u;
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const processGoogleSession = async (sessionId) => {
    const res = await axios.post(`${API}/auth/google/session`, { session_id: sessionId });
    const { token: t, user: u } = res.data;
    if (t) {
      localStorage.setItem('m3_token', t);
      setToken(t);
    }
    setUser(u || res.data);
    return u || res.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {});
    } catch {}
    localStorage.removeItem('m3_token');
    setToken(null);
    setUser(null);
  };

  const updateProfile = async (data) => {
    const res = await axiosAuth().put('/auth/profile', data);
    const updatedUser = res.data.user || res.data;
    setUser(updatedUser);
    return updatedUser;
  };

  return (
    <AuthContext.Provider value={{ user, loading, token, permissions, login, register, loginWithGoogle, processGoogleSession, logout, updateProfile, axiosAuth, API }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
